import { useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import {
  Alert,
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  Modal,
  Skeleton,
  Svg,
  SvgBubble,
} from '@hopskipdrive/react-components';
import {
  CAR_SEAT_AUTO_SELECT_MAX_AGE,
  HIDDEN_SERVICE_CODES,
  MIN_CAR_SEAT_AGE,
} from '@rideiq/riders/constants/riderServices';
import { PageHeading } from '@rideiq/shared/components/PageHeading';
import parse from 'html-react-parser';

import { getRiderServices } from '../../api/services/RiderServices';

import { CloseButton } from './RiderServices.styles';

export const RiderServices = ({ isInitialized, setIsInitialized, riderState = '', dateOfBirth, editFlow }) => {
  const [services, setServices] = useState<RiderService[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [requestServicesAlert, setRequestServicesAlert] = useState<string>('');
  const isPaused = riderState === 'paused';
  const [description, setDescription] = useState<string | undefined>(undefined);

  const { control, setValue, getValues } = useFormContext();

  useEffect(() => {
    const fetchServices = async () => {
      const response = await getRiderServices();

      if (response?.type === 'success') {
        setServices(response.data.services);
        setRequestServicesAlert(getUnavailableServiceAlert(response.data.services, isPaused));
        setLoading(false);
      }
    };

    fetchServices();
  }, []);

  function calculateAge(birthDateString: string): number {
    const birthDate = new Date(birthDateString);
    const today = new Date();

    let age = today.getFullYear() - birthDate.getFullYear();
    // Adjust age if the birth date hasn't occurred yet this year
    const monthDifference = today.getMonth() - birthDate.getMonth();
    const dayDifference = today.getDate() - birthDate.getDate();

    if (monthDifference < 0 || (monthDifference === 0 && dayDifference < 0)) {
      age--;
    }

    return age;
  }

  const riderAge = calculateAge(dateOfBirth);

  useEffect(() => {
    // Here we are getting the list of services selected in the form
    const currentServices = getValues('services');
    // Acceptable age range to auto select CS service
    const ageInRange = riderAge >= MIN_CAR_SEAT_AGE && riderAge <= CAR_SEAT_AUTO_SELECT_MAX_AGE;
    // Making sure CS service is available in the list of services
    const csServiceAvailable = services.some((service) => service.code === 'CS' && service.available);
    // if the form includes the CS service
    const hasCSService = currentServices.includes('CS');

    if (!editFlow && csServiceAvailable && !isInitialized) {
      if (ageInRange && !hasCSService) {
        setValue('services', [...currentServices, 'CS']);
        setIsInitialized(true); // Set initialized to true after the first run
      } else if (riderAge < MIN_CAR_SEAT_AGE && hasCSService) {
        setValue(
          'services',
          currentServices.filter((service) => service !== 'CS')
        );
      }
    }
  }, [riderAge, services]);

  return (
    <Box display="flex" flexDirection="column">
      <Divider mt={'2rem'} mb={'2rem'} />
      <PageHeading variant="h2">Services</PageHeading>
      {requestServicesAlert && <Alert sx={{ m: '10px 0' }} severity="info" message={requestServicesAlert} />}
      {loading ? (
        <LoadingState />
      ) : (
        <Controller
          control={control}
          name="services"
          defaultValue={[]}
          render={({ field: { onChange, value } }) => {
            const codes = value.map((service) => (typeof service === 'string' ? service : service.code));

            return (
              <>
                {services &&
                  services
                    .filter((service) => !HIDDEN_SERVICE_CODES.includes(service.code))
                    .map((service, index) => (
                      <Box key={index} display="flex" flexDirection="row" alignItems="center">
                        <FormControlLabel
                          control={
                            <Checkbox
                              sx={{ ml: '.5rem' }}
                              id={`service-${service.code}}`}
                              data-testid={`service-${service.code}}`}
                              value={service.code}
                              checked={codes.includes(service.code)}
                              onChange={(event, checked) => {
                                if (checked) {
                                  onChange([...codes, event.target.value]);
                                } else {
                                  onChange(codes.filter((value) => value !== event.target.value));
                                }
                              }}
                            />
                          }
                          label={service.name}
                          disabled={
                            (!codes.includes(service.code) && !service.available) ||
                            isPaused ||
                            (service.code === 'CS' && riderAge < MIN_CAR_SEAT_AGE)
                          }
                        />

                        <SvgBubble onClick={() => setDescription(service.description)}>
                          <Svg icon="question" />
                        </SvgBubble>
                      </Box>
                    ))}
              </>
            );
          }}
        />
      )}
      {description && (
        <Modal open>
          <Modal.Title>
            <Box display="flex" justifyContent="end">
              <CloseButton onClick={() => setDescription(undefined)}>
                <Svg height="20px" icon="cross" />
              </CloseButton>
            </Box>
          </Modal.Title>
          <Modal.Content>{parse(description)}</Modal.Content>
        </Modal>
      )}
    </Box>
  );
};

const LoadingState = () => {
  return (
    <Box display="flex" flexDirection="column">
      <Skeleton width="100%" height="30px" />
      <Skeleton width="100%" height="30px" />
      <Skeleton width="100%" height="30px" />
    </Box>
  );
};

function getUnavailableServiceAlert(services, isPaused) {
  const unavailableServiceNames = services.filter((service) => !service.available).map((service) => service.name);
  let formatted = '';

  if (isPaused) {
    return '';
  }

  if (unavailableServiceNames.length === 1) {
    formatted = `${unavailableServiceNames[0]} must be requested via an external form and will be enabled automatically after the process is completed.`;
  } else if (unavailableServiceNames.length === 2) {
    formatted = `${unavailableServiceNames[0]} and ${unavailableServiceNames[1]} must be requested via an external form and will be enabled automatically after the process is completed.`;
  } else if (unavailableServiceNames.length > 2) {
    const last = unavailableServiceNames.pop();

    formatted = `${unavailableServiceNames.join(
      ', '
    )}, and ${last} must be requested via an external form and will be enabled automatically after the process is completed.`;
  }

  return formatted;
}
